import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchEnrichmentRunFeed } from '@/api/admin'
import type { AdminEnrichmentRun, EnrichmentRunFeed } from '@/types/admin'
import type { PaginatedResponse } from '@/types/api'

/**
 * Matches the reading page's poll. Runs advance at ~1.2s/word (Icelandic) to ~3.6s
 * (German), so 3s keeps an admin inside the 5-second freshness target without
 * meaningful request volume.
 */
const POLL_INTERVAL_MS = 3000

type Meta = PaginatedResponse<AdminEnrichmentRun>['meta']

export interface EnrichmentRunFeedState {
  /** Queued and running runs, in start order — the index is the queue position. */
  active: AdminEnrichmentRun[]
  /** One page of finished runs, newest first. */
  history: AdminEnrichmentRun[]
  meta: Meta | null
  loading: boolean
  error: string | null
  page: number
  setPage: (page: number) => void
  /** Re-read now, so a stop shows up without waiting for the next tick. */
  refresh: () => Promise<void>
}

/**
 * Owns the admin panel's view of every enrichment run.
 *
 * Polls only while something is genuinely in flight and stops the moment the queue
 * empties (FR-006) — a timer outliving the work it watches is a leak, and an idle
 * queue has nothing to report.
 *
 * `useAdminTab` was not reused: it exposes only `data`/`meta`, so `active` would need
 * a generic passthrough, and it has no polling. Adding both would push complexity into
 * the five tabs that need neither (research R9).
 */
export function useEnrichmentRunFeed(enabled: boolean): EnrichmentRunFeedState {
  const [feed, setFeed] = useState<EnrichmentRunFeed | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const load = useCallback(async () => {
    if (!enabled) return
    try {
      const response = await fetchEnrichmentRunFeed(page)
      if (!mounted.current) return
      setFeed(response.data)
      // A message must not outlive the failure that caused it, or the admin watches an
      // error while runs quietly progress behind it.
      setError(null)
    } catch {
      if (mounted.current) setError('Could not read the enrichment runs.')
    } finally {
      if (mounted.current) setLoading(false)
    }
  }, [enabled, page])

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    void load()
  }, [enabled, load])

  const active = feed?.active ?? []

  useEffect(() => {
    if (!enabled || active.length === 0) return

    const timer = setInterval(() => void load(), POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [enabled, active.length, load])

  return {
    active,
    history: feed?.data ?? [],
    meta: feed?.meta ?? null,
    loading,
    error,
    page,
    setPage,
    refresh: load,
  }
}
